// WhatsApp Users dashboard — /whatsapp/users
// Lists every WhatsApp user with tier badges, search/filter, and a detail
// drawer with full chat history + intent breakdown + timeline + feedback.
// Built 2026-05-16 alongside the email-MFA registration security layer.

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Search, RefreshCw, MessageSquare, Phone, Mail, ChevronRight,
  ChevronLeft, Loader2, Building2, Award, Star, Sprout, Globe,
  ShieldCheck, ShieldOff, Clock, TrendingUp, X,
} from "lucide-react";
import WhatsAppThreatWidget from "../components/WhatsAppThreatWidget";

const TIER_META = {
  master: { label: "Master", icon: Award, color: "bg-amber-100 text-amber-700 border-amber-200" },
  regular: { label: "Regular", icon: Star, color: "bg-blue-100 text-blue-700 border-blue-200" },
  beginner: { label: "Beginner", icon: Sprout, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const STATUS_META = {
  active: { label: "Active", color: "bg-emerald-500" },
  idle: { label: "Idle", color: "bg-amber-400" },
  dormant: { label: "Dormant", color: "bg-slate-400" },
};

const TYPE_META = {
  registered: { label: "REG", color: "bg-emerald-100 text-emerald-700", icon: ShieldCheck },
  guest: { label: "GUEST", color: "bg-amber-100 text-amber-700", icon: ShieldOff },
  staff: { label: "STAFF", color: "bg-violet-100 text-violet-700", icon: ShieldCheck },
};

export default function WhatsAppUsersPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, verified: 0, guests: 0, active_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [selectedPhone, setSelectedPhone] = useState(null);

  useEffect(() => { fetchUsers(); }, [tier, statusF, typeF]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (search.trim()) params.append("search", search.trim());
      if (tier !== "all") params.append("tier", tier);
      if (statusF !== "all") params.append("status", statusF);
      if (typeF !== "all") params.append("customer_type", typeF);
      const r = await axios.get(`${API_URL}/whatsapp/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(r.data?.users || []);
      setStats(r.data?.stats || { total: 0, verified: 0, guests: 0, active_24h: 0 });
    } catch (e) {
      toast.error("Failed to load: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter(u =>
      !term ||
      (u.phone || "").toLowerCase().includes(term) ||
      (u.profile_name || "").toLowerCase().includes(term) ||
      (u.company_name || "").toLowerCase().includes(term) ||
      (u.verified_email || "").toLowerCase().includes(term)
    );
  }, [users, search]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-700 mb-1 flex items-center gap-1" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-2xl font-bold text-slate-900">WhatsApp Users</h1>
            <p className="text-sm text-slate-500">All people who&apos;ve messaged Roket — verified status, tier scoring, chat history.</p>
          </div>
          <Button onClick={fetchUsers} disabled={loading} variant="outline" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Total users" value={stats.total} icon={MessageSquare} color="text-slate-700" />
          <StatTile label="Verified" value={stats.verified} icon={ShieldCheck} color="text-emerald-600" />
          <StatTile label="Guests" value={stats.guests} icon={ShieldOff} color="text-amber-600" />
          <StatTile label="Active in 24h" value={stats.active_24h} icon={TrendingUp} color="text-blue-600" />
        </div>

        {/* Threat Center widget — full */}
        <WhatsAppThreatWidget compact={false} autoRefreshSec={60} />

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search phone, name, company or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterChips label="Tier" value={tier} onChange={setTier} options={[
              { id: "all", label: "All tiers" },
              { id: "master", label: "🏆 Master" },
              { id: "regular", label: "⭐ Regular" },
              { id: "beginner", label: "🌱 Beginner" },
            ]} />
            <FilterChips label="Status" value={statusF} onChange={setStatusF} options={[
              { id: "all", label: "Any status" },
              { id: "active", label: "Active" },
              { id: "idle", label: "Idle" },
              { id: "dormant", label: "Dormant" },
            ]} />
            <FilterChips label="Type" value={typeF} onChange={setTypeF} options={[
              { id: "all", label: "Any type" },
              { id: "registered", label: "Registered" },
              { id: "guest", label: "Guests" },
            ]} />
          </div>
        </div>

        {/* User list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16 text-slate-400 bg-white rounded-xl border">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No users match these filters.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-b">
              <div className="col-span-1"></div>
              <div className="col-span-3">User</div>
              <div className="col-span-3">Company</div>
              <div className="col-span-1 text-right">Msgs</div>
              <div className="col-span-2">Tier</div>
              <div className="col-span-2">Last seen</div>
            </div>
            {filteredUsers.map((u) => (
              <UserRow key={u.phone} user={u} onClick={() => setSelectedPhone(u.phone)} />
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selectedPhone && (
        <UserDetailDrawer
          phone={selectedPhone}
          token={token}
          onClose={() => setSelectedPhone(null)}
          onDemoted={() => { setSelectedPhone(null); fetchUsers(); }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
        </div>
        <Icon className={`w-5 h-5 ${color} opacity-60`} />
      </div>
    </div>
  );
}

function FilterChips({ label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-xs text-slate-500 mr-1">{label}:</span>
      {options.map(o => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            value === o.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
          data-testid={`chip-${label.toLowerCase()}-${o.id}`}
        >{o.label}</button>
      ))}
    </div>
  );
}

function UserRow({ user, onClick }) {
  const tierMeta = TIER_META[user.tier] || TIER_META.beginner;
  const TierIcon = tierMeta.icon;
  const TypeIcon = (TYPE_META[user.customer_type] || TYPE_META.guest).icon;
  const typeColor = (TYPE_META[user.customer_type] || TYPE_META.guest).color;
  const statusColor = (STATUS_META[user.status] || STATUS_META.dormant).color;

  return (
    <button
      onClick={onClick}
      className="w-full grid grid-cols-12 gap-3 px-4 py-3 text-left hover:bg-slate-50 border-b last:border-b-0 items-center"
      data-testid={`user-row-${user.phone}`}
    >
      <div className="col-span-1 flex items-center gap-2">
        <span className={`w-2.5 h-2.5 rounded-full ${statusColor}`} title={user.status} />
        <span className="text-[10px] text-slate-400 font-mono">{user.preferred_lang}</span>
      </div>
      <div className="col-span-3 min-w-0">
        <div className="font-medium text-slate-800 truncate">{user.profile_name || "—"}</div>
        <div className="text-xs text-slate-500 font-mono truncate">{user.phone}</div>
      </div>
      <div className="col-span-3 min-w-0">
        <div className="text-sm text-slate-700 truncate">{user.company_name || <span className="text-slate-400 italic">unlinked</span>}</div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${typeColor}`}>
            <TypeIcon className="w-2.5 h-2.5" />{(TYPE_META[user.customer_type] || TYPE_META.guest).label}
          </span>
          {user.verified_email && (
            <span className="text-[10px] text-slate-500 truncate">{user.verified_email}</span>
          )}
        </div>
      </div>
      <div className="col-span-1 text-right">
        <div className="font-mono font-semibold text-slate-800">{user.messages_count}</div>
        <div className="text-[10px] text-slate-400">{user.unique_intents_30d}i 30d</div>
      </div>
      <div className="col-span-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tierMeta.color}`}>
          <TierIcon className="w-3 h-3" />{tierMeta.label}
        </span>
      </div>
      <div className="col-span-2 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-500">
          {user.last_seen_at ? new Date(user.last_seen_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </button>
  );
}

function UserDetailDrawer({ phone, token, onClose, onDemoted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [windowOpen, setWindowOpen] = useState(true);

  useEffect(() => { fetchDetail(); }, [phone]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/whatsapp/users/${encodeURIComponent(phone)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
      const lastInbound = (r.data?.history || []).filter(h => h.role === "user").slice(-1)[0];
      setWindowOpen(
        lastInbound?.timestamp
          ? (Date.now() - new Date(lastInbound.timestamp).getTime()) <= 24 * 3600 * 1000
          : false
      );
    } catch (e) {
      toast.error("Failed to load detail");
    } finally {
      setLoading(false);
    }
  };

  const demote = async () => {
    const proceed = window.confirm("Demote this user back to guest? They'll need to re-register via email-MFA to regain access.");
    if (!proceed) return;
    try {
      await axios.post(`${API_URL}/whatsapp/users/${encodeURIComponent(phone)}/demote`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User demoted to guest");
      onDemoted();
    } catch (e) {
      toast.error("Demote failed");
    }
  };

  // 24h Meta service-window state is computed in fetchDetail (windowOpen).

  const sendReply = async () => {
    const msg = reply.trim();
    if (!msg) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/whatsapp/users/${encodeURIComponent(phone)}/send`,
        { message: msg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReply("");
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success("Reply sent via WhatsApp");
      fetchDetail();
    } catch (e) {
      toast.error("Send failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose} data-testid="user-detail-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{data?.user?.profile_name || phone}</h2>
            <p className="text-xs text-slate-500 font-mono">{phone}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-drawer">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Identity block */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <DetailField icon={Phone} label="Phone" value={data.user.phone} />
                <DetailField icon={Building2} label="Company" value={data.user.company_name || "—"} />
                <DetailField icon={Mail} label="Verified email" value={data.user.verified_email || "—"} />
                <DetailField icon={Globe} label="Language" value={(data.user.preferred_lang || "en").toUpperCase()} />
                <DetailField icon={Clock} label="First seen" value={data.user.first_seen_at ? new Date(data.user.first_seen_at).toLocaleString() : "—"} />
                <DetailField icon={Clock} label="Last seen" value={data.user.last_seen_at ? new Date(data.user.last_seen_at).toLocaleString() : "—"} />
              </div>
              <div className="flex items-center gap-2 pt-2 border-t">
                <TierBadge tier={data.user.tier} />
                <TypeBadge type={data.user.customer_type} />
                <span className="text-xs text-slate-600">
                  {data.user.messages_count} msgs · {data.user.unique_intents_30d} intents (30d)
                </span>
                {data.user.customer_type === "registered" && (
                  <Button size="sm" variant="outline" onClick={demote} className="ml-auto text-red-600 border-red-200" data-testid="demote-btn">
                    Demote to guest
                  </Button>
                )}
              </div>
            </div>

            {/* Timeline (last 30d sparkline) */}
            {data.timeline?.length > 0 && (
              <DetailSection title="Daily activity (30 days)">
                <ActivitySparkline data={data.timeline} />
              </DetailSection>
            )}

            {/* Intent breakdown */}
            {Object.keys(data.intent_breakdown || {}).length > 0 && (
              <DetailSection title="Top intents (30 days)">
                <div className="space-y-1">
                  {Object.entries(data.intent_breakdown).slice(0, 8).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-sm py-1">
                      <span className="text-slate-700 font-mono text-xs">{k}</span>
                      <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </DetailSection>
            )}

            {/* Feedback received */}
            {data.feedback?.length > 0 && (
              <DetailSection title={`Feedback received (${data.feedback.length})`}>
                <div className="space-y-2">
                  {data.feedback.map((f, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-amber-700 uppercase">{f.kind}</span>
                        <span className="text-xs text-slate-500">
                          {new Date(f.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{f.text}</p>
                    </div>
                  ))}
                </div>
              </DetailSection>
            )}

            {/* Chat history */}
            <DetailSection title={`Recent chat (${data.history?.length || 0} messages)`}>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {(data.history || []).map((h, i) => {
                  const isUser = h.role === "user";
                  return (
                    <div key={i} className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[80%] rounded-lg p-2 text-xs ${
                        isUser ? "bg-slate-100 text-slate-800" : "bg-blue-50 text-blue-900"
                      }`}>
                        <p className="opacity-60 text-[10px] mb-0.5">
                          {h.role || "user"} · {h.timestamp ? new Date(h.timestamp).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                          {h.intent && <span className="ml-1 font-mono">({h.intent})</span>}
                        </p>
                        <p className="whitespace-pre-wrap break-words">{h.message || h.text || ""}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DetailSection>

            {/* Reply composer — staff → customer WhatsApp message */}
            <DetailSection title="Reply to customer on WhatsApp">
              {!windowOpen && (
                <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="wa-window-warning">
                  ⚠️ The customer&apos;s 24-hour WhatsApp window may be closed. Free-text replies are only guaranteed to deliver within 24h of their last message.
                </div>
              )}
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type a message to send to the customer on WhatsApp..."
                rows={3}
                className="text-sm"
                data-testid="wa-reply-input"
              />
              <div className="flex justify-end mt-2">
                <Button
                  onClick={sendReply}
                  disabled={!reply.trim() || sending}
                  size="sm"
                  data-testid="wa-reply-send-btn"
                >
                  {sending ? "Sending…" : "Send via WhatsApp"}
                </Button>
              </div>
            </DetailSection>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="text-sm font-medium text-slate-800 truncate">{value}</div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <h3 className="font-semibold text-sm text-slate-800 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function TierBadge({ tier }) {
  const meta = TIER_META[tier] || TIER_META.beginner;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
      <Icon className="w-3 h-3" />{meta.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.guest;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${meta.color}`}>
      <Icon className="w-3 h-3" />{meta.label}
    </span>
  );
}

function ActivitySparkline({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => {
        const h = (d.count / max) * 100;
        return (
          <div
            key={i}
            className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors min-w-0"
            style={{ height: `${Math.max(h, 4)}%` }}
            title={`${d.date}: ${d.count} msgs`}
          />
        );
      })}
    </div>
  );
}
